import { useQueryClient } from '@tanstack/react-query'
import type { ChangeEvent } from 'react'
import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { FormNumberFieldDS, FormSelectFieldDS, FormTelephoneInputDS, FormTextFieldDS } from 'components/reusable/form/FormFields'
import { SlabAddressFields } from 'components/reusable/form/SlabAddressFields'
import { Banner } from 'slab'
import {
  getGetQuotingProspectsIdQueryKey,
  getGetQuotingProspectsQueryKey,
  putQuotingProspectsProspectIdCensusIdAssignToProspect as assignCensusToProspect,
  usePostQuotingProspects,
  usePutQuotingProspectsId
} from 'utils/web_api'
import type { PutQuotingProspectsId, PutQuotingProspectsIdBusinessType, PutQuotingProspectsIdState, QuotingProspect } from 'utils/web_api.schemas'

import QuoteFlowButtonNav from '../QuoteFlowButtonNav'
import QuoteFlowHeader from '../QuoteFlowHeader'
import QuoteInfoSection from '../QuoteInfoSection'
import { type MetadataConfiguration } from '../types'

/**
 * Employee count threshold for large group quote request flow.
 * Groups with employee_count > this threshold are routed to the quote request flow.
 */
export const LARGE_GROUP_EMPLOYEE_THRESHOLD = 50

type EmployeeCountFieldProps = {
  register: any
  errors: any
  isLoading: boolean
}

const EmployeeCountField = ({ register, errors, isLoading }: EmployeeCountFieldProps) => (
  <FormNumberFieldDS<FormProps>
    fieldId='employee_count'
    fieldLabel='Number of eligible employees'
    description='Required'
    register={register}
    placeholder='Number of employees'
    validationRule={{
      required: 'Number of eligible employees is required.'
    }}
    error={errors.employee_count}
    disabled={isLoading}
  />
)

const formDataFromPostQuotingProspects = (prospect: FormProps, context: QuotingContext) => {
  const fd = new FormData()
  if (prospect.name) { fd.append('name', prospect.name) }
  if (prospect.email) { fd.append('email', prospect.email) }
  if (prospect.dba) { fd.append('dba', prospect.dba) }
  if (prospect.ein) { fd.append('ein', prospect.ein) }
  if (prospect.employee_count) { fd.append('employee_count', prospect.employee_count.toString()) }
  if (prospect.expected_enrollment) { fd.append('expected_enrollment', prospect.expected_enrollment.toString()) }
  if (prospect.business_type) { fd.append('business_type', prospect.business_type) }
  if (prospect.address_line1) { fd.append('address_line1', prospect.address_line1) }
  if (prospect.address_line2) { fd.append('address_line2', prospect.address_line2) }
  if (prospect.city) { fd.append('city', prospect.city) }
  if (prospect.state) { fd.append('state', prospect.state) }
  if (prospect.zip) { fd.append('zip', prospect.zip) }
  if (prospect.phone_number) { fd.append('phone_number', prospect.phone_number) }
  if (context === 'tfb') {
    fd.append('create_sfdc_records', 'true')
    if (!!prospect.employee_count && prospect.employee_count >= 50) {
      fd.append('notify_sales', 'true')
    }
    if (!!prospect.employee_count && prospect.employee_count > LARGE_GROUP_EMPLOYEE_THRESHOLD) {
      fd.append('is_quote_request', 'true')
    }
  }
  if (prospect.partner_metadata) {
    Object.entries(prospect.partner_metadata).forEach(([k, v]) => {
      if (v !== undefined && v !== null) {
        fd.append(`partner_metadata[${k}]`, String(v))
      }
    })
  }
  return fd
}

export type QuotingContext = 'admin' | 'tfb'

export type CreateOrUpdateProspectProps = {
  prospect?: QuotingProspect
  businessTypeOptions: { label: string; value: string }[]
  mapboxAccessToken: string
  onSuccess: (prospect: QuotingProspect) => void
  context: QuotingContext
  autoImportCensusId?: string
  showExpectedEnrollment?: boolean
  metadataConfiguration?: MetadataConfiguration
  requireAddress?: boolean
}

type FormProps = {
  name: string;
  email: string;
  dba: string;
  ein: string;
  employee_count: number;
  expected_enrollment?: number;
  business_type: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zip: string;
  phone_number: string;
  partner_metadata?: { [key: string]: unknown } | null;
  archived?: boolean;
}

export function CreateOrUpdateProspect ({
  prospect,
  businessTypeOptions = [],
  mapboxAccessToken,
  onSuccess,
  context,
  autoImportCensusId = undefined,
  showExpectedEnrollment = false,
  metadataConfiguration,
  requireAddress = false,
}: CreateOrUpdateProspectProps) {
  const [submitError, setSubmitError] = useState<string | undefined>()
  const queryClient = useQueryClient()

  const createProspectBodyRef = useRef<FormData | null>(null)
  const createProspectMutation = usePostQuotingProspects({
    mutation: {
      onSuccess: async (prospect: QuotingProspect) => {
        if (autoImportCensusId) {
          try {
            await assignCensusToProspect(prospect.id, autoImportCensusId)
          } catch {
            setSubmitError('Failed to assign census to prospect. You are attempting to auto-import a census from CADE, but it has already been assigned to a prospect.')
            return
          }
        }
        try {
          await queryClient.invalidateQueries({
            queryKey: getGetQuotingProspectsQueryKey(),
          })
        } catch {}
        onSuccess(prospect)
      },
      onError: (error: unknown) => {
        const errorObj = error as GrapeAPIErrorType
        setSubmitError(errorObj.errors?.join(', ') ?? 'Failed to create prospect')
      },
    },
    request: {
      get body () {
        return createProspectBodyRef.current
      },
    },
  }, queryClient)
  const updateProspectMutation = usePutQuotingProspectsId({
    mutation: {
      onSuccess: async (prospect: QuotingProspect) => {
        try {
          await queryClient.invalidateQueries({
            queryKey: getGetQuotingProspectsIdQueryKey(prospect.id),
          })
        } catch {}
        onSuccess(prospect)
      },
      onError: (error: unknown) => {
        const errorObj = error as GrapeAPIErrorType
        setSubmitError(errorObj.errors?.join(', ') ?? 'Failed to update prospect')
      },
    },
  }, queryClient)

  const defaultValues: Partial<FormProps> = {
    name: prospect?.name || undefined,
    email: prospect?.email || undefined,
    dba: prospect?.dba || undefined,
    business_type: prospect?.business_type || undefined,
    ein: prospect?.ein || undefined,
    employee_count: prospect?.employee_count || undefined,
    expected_enrollment: prospect?.expected_enrollment || undefined,
    address_line1: prospect?.address_line1 || '',
    address_line2: prospect?.address_line2 || undefined,
    city: prospect?.city || undefined,
    state: prospect?.state || undefined,
    zip: prospect?.zip || undefined,
    phone_number: prospect?.phone_number || undefined,
    partner_metadata: prospect?.partner_metadata || undefined,
  }

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    setError,
    clearErrors,
    trigger,
    watch,
    formState: { errors },
  } = useForm<FormProps>({
    defaultValues,
  })

  const prospectName = watch('name')
  const addressLine1 = watch('address_line1')
  const city = watch('city')
  const state = watch('state')
  const zip = watch('zip')
  const isLoading = createProspectMutation.isPending || updateProspectMutation.isPending
  const hasCompleteAddress = addressLine1 && city && state && zip
  const isNextDisabled = !prospectName || (requireAddress && !hasCompleteAddress)

  const onSubmit = (data: FormProps) => {
    if (prospect) {
      const apiData: PutQuotingProspectsId = {
        name: data.name,
        email: data.email || undefined,
        dba: data.dba || undefined,
        ein: data.ein || undefined,
        employee_count: data.employee_count || undefined,
        expected_enrollment: data.expected_enrollment || undefined,
        address_line1: data.address_line1 || undefined,
        address_line2: data.address_line2 || undefined,
        city: data.city || undefined,
        state: data.state as PutQuotingProspectsIdState,
        zip: data.zip || undefined,
        phone_number: data.phone_number || undefined,
        partner_metadata: data.partner_metadata || undefined,
        business_type: data.business_type as PutQuotingProspectsIdBusinessType,
        create_sfdc_records: context === 'tfb',
        notify_sales: context === 'tfb' && !!data.employee_count && data.employee_count >= 50,
        archived: data.archived,
      }

      updateProspectMutation.mutate({ id: prospect.id, data: apiData })
    } else {
      const fd = formDataFromPostQuotingProspects(data, context)

      createProspectBodyRef.current = fd
      createProspectMutation.mutate()
    }
  }

  return (
    <div className='flex flex-col max-w-2xl w-full'>
      <QuoteFlowHeader
        title='Business Details'
        description='Enter company and contact information'
      />
      <form id='create-or-update-prospect-form' className='flex flex-col gap-6'>
        <QuoteInfoSection header='Company information'>
          <div className='flex flex-col gap-6'>
            <div className='flex flex-row gap-4 flex-wrap'>
              <div className='flex-[2] min-w-56'>
                <FormTextFieldDS<FormProps>
                  fieldId='name'
                  fieldLabel='Company name'
                  description='Required'
                  register={register}
                  placeholder='Company name'
                  validationRule={{ required: 'Company name is required.' }}
                  error={errors.name}
                  disabled={isLoading}
                />
              </div>
              {!showExpectedEnrollment &&
                <div className='flex-1 min-w-44'>
                  <EmployeeCountField register={register} errors={errors} isLoading={isLoading} />
                </div>}
            </div>
            {showExpectedEnrollment &&
              <div className='flex flex-row gap-4 flex-wrap'>
                <div className='flex-1 min-w-56'>
                  <EmployeeCountField register={register} errors={errors} isLoading={isLoading} />
                </div>
                <div className='flex-1 min-w-56'>
                  <FormNumberFieldDS<FormProps>
                    fieldId='expected_enrollment'
                    fieldLabel='Expected enrolled lives'
                    description='Required'
                    register={register}
                    placeholder='Number of lives'
                    validationRule={{
                      required: 'Expected enrolled lives is required.',
                      validate: (value: string) => {
                        const numEmployeeCount = Number(getValues('employee_count'))
                        const numExpectedEnrolledLives = Number(value)
                        if (numExpectedEnrolledLives > numEmployeeCount) {
                          return 'Expected enrolled lives must be less than or equal to the number of eligible employees'
                        }
                        return true
                      }
                    }}
                    error={errors.expected_enrollment}
                    disabled={isLoading}
                  />
                </div>
              </div>}
            <FormTextFieldDS<FormProps>
              fieldId='dba'
              fieldLabel='DBA'
              register={register}
              placeholder='DBA'
              error={errors.dba}
              disabled={isLoading}
            />
            <div className='flex flex-row gap-4 flex-wrap'>
              <div className='flex-1 min-w-56'>
                <FormSelectFieldDS<FormProps>
                  fieldId='business_type'
                  fieldLabel='Company entity type'
                  fieldOptions={businessTypeOptions}
                  placeholder='Select company entity type'
                  register={register}
                  error={errors.business_type}
                  disabled={isLoading}
                />
              </div>
              <div className='flex-1 min-w-56'>
                <FormTextFieldDS<FormProps>
                  fieldId='ein'
                  fieldLabel='Tax ID number'
                  register={register}
                  placeholder='Tax ID number'
                  error={errors.ein}
                  disabled={isLoading}
                  maxLength={10}
                  validationRule={{
                    pattern: {
                      value: /^\d{2}-\d{7}$/,
                      message: 'Your Tax ID number must be a 9-digit number with the format XX-XXXXXXX',
                    },
                    onChange: (e: ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value.replace(/\D/g, '')
                      if (value.length <= 2) {
                        e.target.value = value
                      } else if (value.length <= 9) {
                        e.target.value = `${value.slice(0, 2)}-${value.slice(2)}`
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </QuoteInfoSection>
        <QuoteInfoSection header='Contact information'>
          <div className='flex flex-col gap-y-4'>
            <FormTextFieldDS<FormProps>
              fieldId='email'
              fieldLabel='Email address'
              register={register}
              placeholder='Email address'
              error={errors.email}
              disabled={isLoading}
            />
            <div className='flex flex-col gap-y-1'>
              <SlabAddressFields<FormProps>
                accessToken={mapboxAccessToken}
                errors={errors}
                getValues={getValues}
                setValue={setValue}
                setError={setError}
                clearErrors={clearErrors}
                register={register}
                watch={watch}
                trigger={trigger}
                disabled={isLoading}
                showRequired={requireAddress}
                validationRules={requireAddress
                  ? {
                      address_line1: { required: 'Street address is required.' },
                      city: { required: 'City is required.' },
                      state: { required: 'State is required.' },
                      zip: { required: 'Zip code is required.' },
                    }
                  : undefined}
              />
            </div>
            <FormTelephoneInputDS<FormProps>
              fieldId='phone_number'
              fieldLabel='Business phone number'
              register={register}
              placeholder='(xxx) xxx-xxxx'
              error={errors.phone_number}
              disabled={isLoading}
            />
          </div>
        </QuoteInfoSection>
        {metadataConfiguration &&
          <QuoteInfoSection header={metadataConfiguration.title} subheader={metadataConfiguration.description}>
            {metadataConfiguration.fields.map((field) => (
              <FormTextFieldDS<FormProps>
                key={field}
                fieldId={`partner_metadata.${field}`}
                fieldLabel={field}
                register={register}
                placeholder={field}
                disabled={isLoading}
              />
            ))}
          </QuoteInfoSection>}
      </form>

      <div className='mt-10 flex flex-col gap-y-4'>
        {submitError && (
          <Banner
            title={`Error ${prospect ? 'updating' : 'creating'} prospect`}
            type='critical'
            onDismiss={() => setSubmitError(undefined)}
          >
            <p>{submitError}</p>
          </Banner>
        )}
        <QuoteFlowButtonNav
          onNext={() => handleSubmit(onSubmit)()}
          isNextDisabled={isNextDisabled}
          isLoading={isLoading}
        />
      </div>
    </div>
  )
}

export default CreateOrUpdateProspect
